// Components
import { DialogMessageComponent } from './components/dialog-message/dialog-message.component';
import { AppComponent } from './app.component';

//Angular Dependencies
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LOCATION_INITIALIZED } from '@angular/common';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { initializeApp } from 'firebase/app';
import {
    NgModule,
    InjectionToken,
    Injector,
    APP_INITIALIZER,
} from '@angular/core';

// NGX Translate
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
    TranslateLoader,
    TranslateModule,
    TranslateService,
} from '@ngx-translate/core';

// NGRX
import { StoreModule, ActionReducer, MetaReducer } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { combinedReducers } from './store/reducers';
import { EffectsModule } from '@ngrx/effects';
import { effects } from './store/effects';

// Routing
import { AppRoutingModule } from './routing/app-routing.module';

// Persister
import { localStorageSync } from 'ngrx-store-localstorage';

// Environment
import { environment } from '../environments/environment';

// Bootstrap
import { GoogleAnalyticsService } from './services/google-analytics.service';

// Social Login
import {
    SocialAuthServiceConfig,
    MicrosoftLoginProvider,
    GoogleLoginProvider,
    SocialLoginModule,
} from '@abacritt/angularx-social-login';

export const ReducerToken = new InjectionToken('Registered Reducer');

export function getReducers(): any {
    return combinedReducers;
}

export function HttpLoaderFactory(http: HttpClient): any {
    return new TranslateHttpLoader(
        http,
        './assets/i18n/',
        `.json?cb=${new Date().getTime()}`
    );
}

function isBase64(str) {
    try {
        return btoa(atob(str)) === str;
    } catch (err) {
        return false;
    }
}

function utf8_to_b64(str) {
    return btoa(unescape(encodeURIComponent(str)));
}

function b64_to_utf8(str) {
    return decodeURIComponent(escape(atob(str)));
}

export function localStorageSyncReducer(
    reducer: ActionReducer<any>
): ActionReducer<any> {
    return localStorageSync({
        keys: [
            {
                user: {
                    encrypt: (state) => utf8_to_b64(state),
                    decrypt: (state) =>
                        isBase64(state) ? b64_to_utf8(state) : state,
                },
            },
            {
                dataBus: {
                    encrypt: (state) => utf8_to_b64(state),
                    decrypt: (state) =>
                        isBase64(state) ? b64_to_utf8(state) : state,
                },
            },
            {
                ui: {
                    encrypt: (state) => utf8_to_b64(state),
                    decrypt: (state) =>
                        isBase64(state) ? b64_to_utf8(state) : state,
                },
            },
            'rtdb',
        ],
        rehydrate: true,
    })(reducer);
}

export function appInitializerFactory(
    translate: TranslateService,
    injector: Injector
) {
    return (): any =>
        new Promise<any>((resolve: any) => {
            const locationInitialized = injector.get(
                LOCATION_INITIALIZED,
                Promise.resolve(null)
            );
            locationInitialized.then(() => {
                const langToSet = 'en';
                translate.setDefaultLang('en');
                translate.use(langToSet).subscribe(
                    () => {
                        // eslint-disable-next-line no-console
                        console.info(
                            `Successfully initialized '${langToSet}' language.'`
                        );
                    },
                    () => {
                        // eslint-disable-next-line no-console
                        console.error(
                            `Problem with '${langToSet}' language initialization.'`
                        );
                    },
                    () => {
                        resolve(null);
                    }
                );
            });
        });
}

const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

initializeApp(environment.firebase);

let devImports = [StoreDevtoolsModule.instrument({ maxAge: 15 })];
if (environment.production) {
    devImports = [];
}

@NgModule({
    declarations: [DialogMessageComponent, AppComponent],
    imports: [
        StoreModule.forRoot(ReducerToken, {
            metaReducers,
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false,
            },
        }),
        devImports,
        EffectsModule.forRoot(effects),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        BrowserAnimationsModule,
        AppRoutingModule,
        HttpClientModule,
        SocialLoginModule,
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFactory,
            deps: [TranslateService, Injector],
            multi: true,
        },
        [{ provide: ReducerToken, useFactory: getReducers }],
        GoogleAnalyticsService,
        {
            provide: 'SocialAuthServiceConfig',
            useValue: {
                autoLogin: false,
                providers: [
                    {
                        id: GoogleLoginProvider.PROVIDER_ID,
                        provider: new GoogleLoginProvider(
                            environment.googleSSOClientId,
                            {
                                oneTapEnabled: false,
                            }
                        ),
                    },
                    {
                        id: MicrosoftLoginProvider.PROVIDER_ID,
                        provider: new MicrosoftLoginProvider(
                            environment.microsoftSSOClientId,
                            {
                                // redirect_uri:
                                //     'http://localhost:4200/dashboard/content/welcome',
                                // logout_redirect_uri: 'http://localhost:4200/login',
                            }
                        ),
                    },
                ],
                onError: (err) => {
                    console.error(err);
                },
            } as SocialAuthServiceConfig,
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
